import { createContext, useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";

export const UserDataContext = createContext(null);

export const UserDataProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [userData, setUserData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Configure axios instance with credentials globally
  axios.defaults.withCredentials = true;

  // --- Fetch user from backend using HTTP-only cookie
  const fetchUser = async () => {
    try {
      const { data } = await axios.get("/api/auth/me", {
        withCredentials: true,
      });

      if (data.success && data.user) {
        console.log("[fetchUser] User authenticated:", data.user);
        setUserData(data.user);
      } else {
        console.warn("[fetchUser] No user found, clearing state");
        setUserData(null);
      }
    } catch (err) {
      console.error(
        "[fetchUser] Error fetching user:",
        err.response?.data || err.message
      );
      setUserData(null);
    } finally {
      setIsInitialized(true);
    }
  };

  // --- Initialize user on mount (restore from cookie)
  useEffect(() => {
    fetchUser();
  }, []);

  // --- Login (backend sets HTTP-only cookie automatically)
  const login = async (credentials) => {
    try {
      const { data } = await axios.post("/api/auth/login", credentials, {
        withCredentials: true,
      });

      if (data.success) {
        // If backend returns authtoken for web client, store it
        // but rely on HTTP-only cookie for API calls
        if (data.authtoken) {
          Cookies.set("authtoken", data.authtoken, {
            expires: 1,
            sameSite: "Lax", // Changed to "Lax" for consistency
            secure: window.location.protocol === "https:", // Better production detection
            path: "/", // Ensure path is set
          });
        }

        // Fetch user details via /api/auth/me
        // This will use the HTTP-only cookie from backend
        await fetchUser();
      }

      return data;
    } catch (err) {
      console.error("[login] Login error:", err);
      throw err;
    }
  };

  // --- Logout
  const logOut = async () => {
    try {
      await axios.post("/api/auth/logout", {}, { withCredentials: true });
    } catch (err) {
      console.error("[logout] Logout error:", err);
    } finally {
      // Clear both authentication and cart cookies
      Cookies.remove("authtoken", { path: "/" });
      // Cookies.remove("cartItems", { path: "/" });

      // Clear user data from state
      setUserData(null);

      // Optional: Clear cart from context if you have access to it
      // clearCart();

      navigate("/");
    }
  };

  // --- Auto redirect logic based on role + route
  useEffect(() => {
    if (!isInitialized) return;

    const path = location.pathname;
    const isAuthRoute =
      path.includes("login") ||
      path.includes("register") ||
      path.includes("bloggerProfile");

    if (userData?.email && isAuthRoute) {
      if (userData.role === "admin") {
        navigate("/", { replace: true });
      } else if (userData.role === "blogger") {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [isInitialized, location.pathname, userData, navigate]);

  return (
    <UserDataContext.Provider
      value={{
        userData,
        setUserData,
        isInitialized,
        fetchUser,
        login,
        logOut,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export default UserDataProvider;
